import React from "react";
import styled from "styled-components";
import { FaCheckCircle } from "react-icons/fa";
import { Helmet } from "react-helmet-async";
const Container = styled.div`
  max-width: 1100px;
  margin: 80px auto;
  padding: 20px;
  font-family: 'Segoe UI', sans-serif;
  color: #333;
`;

const Title = styled.h1`
  font-size: 2.4rem;
  color: ${({ theme }) => theme.colors.primary || "#2e9694"};
  margin-bottom: 10px;
`;

const SubTitle = styled.h2`
  font-size: 1.5rem;
  margin: 30px 0 15px;
  color: ${({ theme }) => theme.colors.dark || "#222"};
`;

const Text = styled.p`
  font-size: 1rem;
  line-height: 1.7;
  margin-bottom: 20px;
`;

const CertSection = styled.section`
  margin-top: 40px;
`;

const CertTitle = styled.h2`
  font-size: 1.8rem;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 20px;
`;

const CertGrid = styled.div`
  display: grid;
  gap: 20px;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
`;

const CertCard = styled.div`
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  box-shadow: 0 4px 12px rgba(0,0,0,0.06);
  transition: 0.3s;

  &:hover {
    box-shadow: 0 6px 20px rgba(0,0,0,0.1);
    transform: translateY(-5px);
  }

  img {
    width: 60px;
    margin-bottom: 15px;
  }

  h3 {
    font-size: 1.1rem;
    color: ${({ theme }) => theme.colors.dark};
    margin-bottom: 10px;
  }

  p {
    font-size: 0.9rem;
    color: #666;
  }
`;

const Check = styled(FaCheckCircle)`
  color: ${({ theme }) => theme.colors.primary || "#2e9694"};
  margin-right: 8px;
`;

const PrivacyPolicy = () => {
  return (
    <>
     <Helmet>
        <title>Privacy Policy | Circutech</title>
        <meta
          name="description"
          content="Read Circutech's Privacy Policy to understand how we protect your personal data and ensure transparency in our practices."
        />
        <meta
          name="keywords"
          content="privacy policy, Circutech data protection, GDPR, user privacy"
        />
      </Helmet>
    <Container>
      <Title>Privacy Policy</Title>
      <Text>
        At Circuitech, we respect your privacy and are committed to protecting your personal data.
        This Privacy Policy outlines how we collect, use, and protect your information when you interact with our services.
      </Text>

      <SubTitle>Information We Collect</SubTitle>
      <Text>
        We may collect personal information such as your name, email, phone number, company name, and message details when you contact us.
        This information is only used to respond to your queries and improve our services.
      </Text>

      <SubTitle>How We Use Your Information</SubTitle>
      <Text>
        <Check /> To provide and manage our services  
        <br />
        <Check /> To respond to your inquiries or requests  
        <br />
        <Check /> To maintain records for legal or business purposes  
        <br />
        <Check /> To improve website functionality and user experience  
      </Text>

      <SubTitle>Data Protection</SubTitle>
      <Text>
        We implement strict data protection practices and access controls to ensure your personal information is secure and never misused.
        Our team follows globally recognized standards and certifications.
      </Text>

      <CertSection>
        <CertTitle>Our ISO Certifications</CertTitle>
        <CertGrid>
          <CertCard>
            <img src="https://cdn-icons-png.flaticon.com/512/992/992700.png" alt="ISO 9001" />
            <h3>ISO 9001</h3>
            <p>Quality Management System ensuring consistent performance and customer satisfaction.</p>
          </CertCard>
          <CertCard>
            <img src="https://cdn-icons-png.flaticon.com/512/2594/2594707.png" alt="ISO 14001" />
            <h3>ISO 14001</h3>
            <p>Environmental Management standards to support sustainability and reduce impact.</p>
          </CertCard>
          <CertCard>
            <img src="https://cdn-icons-png.flaticon.com/512/859/859270.png" alt="ISO 45001" />
            <h3>ISO 45001</h3>
            <p>Occupational Health & Safety standards for workforce protection and safety.</p>
          </CertCard>
          <CertCard>
            <img src="https://cdn-icons-png.flaticon.com/512/3281/3281307.png" alt="ISO/IEC 27001" />
            <h3>ISO/IEC 27001</h3>
            <p>Information Security standard for data protection and cyber resilience.</p>
          </CertCard>
        </CertGrid>
      </CertSection>

      <SubTitle>Contact Us</SubTitle>
      <Text>
        If you have any questions about this policy or your personal data, feel free to reach out to us:
      </Text>
      <Text>
        <strong>Address:</strong> E.No.2, Ground Floor, Al Zahia Area,<br />
        Sheikh Mohammed Bin Zayed Road, SPC-FZ, Sharjah, UAE<br />
        <strong>Email:</strong> sales@circuitech.ae<br />
        <strong>Phone:</strong> +971 58 597 1951
      </Text>

    </Container>
    </>
  );
};

export default PrivacyPolicy;
